import { useState } from "react";
import { Check, Copy, Eye, Terminal, Webhook } from "lucide-react";
import Reveal from "@/components/Reveal";
import DevPlayground from "@/components/DevPlayground";

const BASE = process.env.REACT_APP_BACKEND_URL;

const SNIPPETS = {
  rest: [
    {
      label: "1 · Create an API key",
      code: `curl -X POST ${BASE}/api/keys \\
  -H 'Content-Type: application/json' \\
  -d '{"name":"my-service"}'`,
      note: "Returns the key once — store it. All v1 calls need it in the X-API-Key header.",
    },
    {
      label: "2 · Create a search",
      code: `curl -X POST ${BASE}/api/v1/searches \\
  -H 'X-API-Key: dsk_live_...' \\
  -H 'Content-Type: application/json' \\
  -d '{"mode":"party","first":"Robert","last":"Callahan",
       "county":"Kings","caseType":"Post Judgment CM",
       "clientRef":"deal-1042"}'`,
      note: "Fires search.created to your webhooks (retried with backoff until delivered) and returns the court handoff your app should surface.",
    },
  ],
  webhooks: [
    {
      label: "3 · Register a webhook",
      code: `curl -X POST ${BASE}/api/webhooks \\
  -H 'X-API-Key: dsk_live_...' \\
  -H 'Content-Type: application/json' \\
  -d '{"url":"https://yourapp.com/hooks/docketscout"}'`,
      note: "You receive a whsec_... signing secret for this endpoint.",
    },
    {
      label: "4 · Receive the event",
      code: `POST https://yourapp.com/hooks/docketscout
X-DocketScout-Event: search.created
X-DocketScout-Signature: sha256=<hmac>

{
  "id": "evt_9f2c1ab7",
  "type": "search.created",
  "created_at": "2026-10-06T12:00:00Z",
  "data": {
    "search_id": "…",
    "mode": "party",
    "party": "Robert Callahan",
    "court_url": "https://iapps.courts.state.ny.us/…",
    "client_ref": "deal-1042"
  }
}`,
      note: "Signature = HMAC-SHA256 of the raw body with your webhook secret. Failed deliveries retry with backoff and land in the delivery log.",
    },
  ],
  watches: [
    {
      label: "1 · Watch a party",
      code: `curl -X POST ${BASE}/api/v1/watches \\
  -H 'X-API-Key: dsk_live_...' \\
  -H 'Content-Type: application/json' \\
  -d '{"mode":"party","first":"Robert","last":"Callahan",
       "county":"Kings","clientRef":"deal-1042"}'`,
      note: "Watches belong to your API key and notify every webhook registered under it.",
    },
    {
      label: "2 · Push a match from your case source",
      code: `curl -X POST ${BASE}/api/v1/watches/{id}/matches \\
  -H 'X-API-Key: dsk_live_...' \\
  -H 'Content-Type: application/json' \\
  -d '{"case_number":"012345/2026",
       "court":"Kings County Supreme Court",
       "case_type":"Post Judgment CM",
       "filed_date":"2026-10-06"}'`,
      note: "DocketScout never polls the court — their security blocks automated access. Point your case source (scraping service, manual reviews) at this endpoint and we handle signing, fan-out and retries.",
    },
    {
      label: "3 · Receive case.matched",
      code: `X-DocketScout-Event: case.matched
X-DocketScout-Signature: sha256=<hmac>

{
  "type": "case.matched",
  "data": {
    "watch_id": "…",
    "client_ref": "deal-1042",
    "party": "Robert Callahan",
    "match": {
      "case_number": "012345/2026",
      "court": "Kings County Supreme Court",
      "case_type": "Post Judgment CM"
    }
  }
}`,
      note: "Retry schedule: 1 min → 5 min → 20 min, then marked exhausted. Replay any delivery from the log.",
    },
  ],
};

const ENDPOINTS = [
  ["POST", "/api/keys", "Create an API key", false],
  ["POST", "/api/v1/searches", "Create a search, get the court handoff", true],
  ["GET", "/api/v1/searches/{id}", "Fetch a search", true],
  ["POST", "/api/v1/watches", "Watch a party, get case.matched events", true],
  ["GET", "/api/v1/watches", "List watches", true],
  ["POST", "/api/v1/watches/{id}/matches", "Push a match; fires signed webhooks", true],
  ["POST", "/api/webhooks", "Register a webhook endpoint", true],
  ["POST", "/api/webhooks/{id}/test", "Send a signed test ping", true],
  ["GET", "/api/deliveries", "Webhook delivery log with retry status", true],
  ["POST", "/api/deliveries/{id}/replay", "Replay a delivery", true],
  ["GET", "/api/dev/docs", "Machine-readable endpoint docs", false],
];

function CodeBlock({ code, label }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };
  return (
    <div className="overflow-hidden rounded-md border border-white/10 bg-black/40" data-testid="dev-code-block">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-ivory/50">{label}</span>
        <button
          onClick={copy}
          data-testid="dev-copy-button"
          className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-semibold text-ivory/60 transition-colors hover:bg-white/10 hover:text-ivory"
        >
          {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="overflow-x-auto p-4 font-mono text-[12px] leading-relaxed text-ivory/85">{code}</pre>
    </div>
  );
}

export default function Developers() {
  const [tab, setTab] = useState("rest");

  return (
    <section id="developers" className="scroll-mt-24 bg-ink px-5 py-24 text-ivory md:px-8 md:py-32" data-testid="developers-section">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.22em] text-crimson">For developers</p>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <h2 className="max-w-xl font-display text-4xl leading-[1.1] tracking-tight md:text-5xl">
              API &amp; webhooks, so your systems <em className="text-crimson">talk to us.</em>
            </h2>
            <p className="max-w-md text-sm leading-relaxed text-ivory/60">
              Create searches and case watches programmatically, receive signed
              webhooks with automatic retries, and audit every delivery — same
              flows as the site.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex gap-6 border-b border-white/10 text-sm font-semibold" data-testid="dev-tabs">
            {[
              ["rest", "REST API", Terminal],
              ["webhooks", "Webhooks", Webhook],
              ["watches", "Case watches", Eye],
            ].map(([id, label, Icon]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`dev-tab-${id}`}
                className={`flex items-center gap-2 pb-3 transition-colors ${tab === id ? "border-b-2 border-crimson text-ivory" : "text-ivory/45 hover:text-ivory"}`}
              >
                <Icon size={15} />
                {label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 grid gap-10 lg:grid-cols-12">
          <div className="min-w-0 lg:col-span-7">
            <Reveal>
              <div className="space-y-5" data-testid={`dev-snippets-${tab}`}>
                {SNIPPETS[tab].map((s) => (
                  <div key={s.label}>
                    <CodeBlock code={s.code} label={s.label} />
                    <p className="mt-2 text-xs leading-relaxed text-ivory/50">{s.note}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-8 overflow-hidden rounded-md border border-white/10" data-testid="dev-endpoints-table">
                <table className="w-full text-left text-[13px]">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5 text-[11px] uppercase tracking-[0.14em] text-ivory/50">
                      <th className="px-4 py-3 font-semibold">Method</th>
                      <th className="px-4 py-3 font-semibold">Endpoint</th>
                      <th className="hidden px-4 py-3 font-semibold sm:table-cell">Description</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {ENDPOINTS.map(([m, p, d, needsKey]) => (
                      <tr key={p} className="border-b border-white/5 last:border-0 hover:bg-white/5">
                        <td className="px-4 py-3">
                          <span className={`rounded px-2 py-0.5 text-[11px] font-bold ${m === "GET" ? "bg-white/10 text-ivory" : "bg-crimson text-ivory"}`}>{m}</span>
                        </td>
                        <td className="px-4 py-3 text-ivory/85">{p}{needsKey && <span className="ml-2 rounded border border-white/15 px-1.5 py-0.5 text-[10px] text-ivory/50">key</span>}</td>
                        <td className="hidden px-4 py-3 font-sans text-ivory/60 sm:table-cell">{d}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Reveal>
          </div>

          <div className="min-w-0 lg:col-span-5">
            <Reveal delay={0.15}>
              <DevPlayground />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
